import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { Controller, type Resolver, useForm } from "react-hook-form";

import { contractShipmentDeliveryFormSchema } from "../schema/ContractShipmentDeliveryForm.schema";

import {
  ContractShipmentStatus,
  type ListContractShipmentsResponseItem,
  type UpdateContractShipmentStatusRequest
} from "@/interfaces/modules/contract-shipments";

interface ContractShipmentDeliveryDialogProps {
  shipment: ListContractShipmentsResponseItem;
  isSaving: boolean;
  onDismiss: () => void;
  onUpdateStatus: (
    contractShipmentId: number,
    data: UpdateContractShipmentStatusRequest,
    messages: Messages
  ) => Promise<boolean>;
}

interface Messages {
  success: string;
  error: string;
}

interface DeliveryFormValues {
  receivedAt: string;
}

export function ContractShipmentDeliveryDialog({
  shipment,
  isSaving,
  onDismiss,
  onUpdateStatus
}: ContractShipmentDeliveryDialogProps): JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<DeliveryFormValues>({
    resolver: yupResolver(
      contractShipmentDeliveryFormSchema
    ) as Resolver<DeliveryFormValues>,
    defaultValues: { receivedAt: "" },
    mode: "onChange"
  });
  const isDisabled = isSaving || isSubmitting;
  const today = format(new Date(), "yyyy-MM-dd");

  async function confirmShipmentDelivery({ receivedAt }: DeliveryFormValues) {
    const wasConfirmed = await onUpdateStatus(
      shipment.id,
      {
        status: ContractShipmentStatus.COMPLETED,
        receivedAt
      },
      {
        success: "Entrega confirmada com sucesso.",
        error: "Não foi possível confirmar a entrega."
      }
    );

    if (wasConfirmed) onDismiss();
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Confirmar entrega",
        closeButtonAriaLabel: "Fechar"
      }}
      modalProps={{ isBlocking: true }}
      minWidth={420}
    >
      <form
        onSubmit={event => void handleSubmit(confirmShipmentDelivery)(event)}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text>Informe a data de entrega do envio {shipment.code}.</Text>

          <Controller
            name="receivedAt"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Data de entrega"
                type="date"
                max={today}
                required
                disabled={isDisabled}
                value={field.value}
                onBlur={field.onBlur}
                onChange={(_, value) => field.onChange(value ?? "")}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <DialogFooter>
          <DefaultButton
            text="Cancelar"
            type="button"
            onClick={onDismiss}
            disabled={isDisabled}
          />
          <PrimaryButton
            text="Confirmar entrega"
            type="submit"
            disabled={!isValid || isDisabled}
          />
        </DialogFooter>
      </form>
    </Dialog>
  );
}
